import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  useOrderCommentsListQuery,
  useLazyOrderCommentsListQuery,
  useOrderCommentCreateMutation,
  useOrderCommentImportantMutation,
} from "../../../Features/order/order-slice";
import {
  BUTTON_LABEL,
  DATE_FORMAT,
  ERROR_MESSAGE,
  LABELS,
  TOAST_MESSAGE,
  icons,
  toastError,
  toastSuccess,
} from "../../constants";
import {
  COMMENT_IMAGE_MIME_TYPES,
  COMMENT_USER_ROLE,
  MAX_COMMENT_IMAGE_SIZE,
  MAX_COMMENT_IMAGES,
  UserType,
} from "../../constants/resource";
import authProvider from "../../config/authProvider";
import ImageLightbox from "../ImageLightbox";


interface OrderChatProps {
  orderId: string;
  documentNo: string;
  isDispatched: boolean;
  isConfirmed?: boolean;
}

interface CommentImage {
  _id?: string;
  fileName?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  isPurged?: boolean;
}

interface Comment {
  _id: string;
  userRole?: string;
  commentText?: string;
  isImportant?: boolean;
  createdAt?: string;
  authorName?: string;
  images?: CommentImage[];
}

const OrderChat = ({
  orderId,
  documentNo,
  isDispatched,
  isConfirmed = true,
}: OrderChatProps) => {
  const [commentText, setCommentText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxImages, setLightboxImages] = useState<CommentImage[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const userData = authProvider.getUserData();
  const isBackOffice =
    userData?.item?.type === UserType.BO ||
    userData?.item?.type === UserType.SUPER;

  const CHAT_PAGE_LIMIT = 20;

  const [commentsList, setCommentsList] = useState<Comment[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [isInitialLoaded, setIsInitialLoaded] = useState<boolean>(false);

  const threadRef = useRef<HTMLDivElement>(null);
  const [fetchCommentsLazy] = useLazyOrderCommentsListQuery();

  const queryArgs = useMemo(
    () => ({ orderId, limit: CHAT_PAGE_LIMIT, skip: 0, sort: "desc" }),
    [orderId]
  );

  const { data, isSuccess, isLoading, refetch } = useOrderCommentsListQuery(queryArgs, {
    pollingInterval: 2000,
    refetchOnMountOrArgChange: true,
  });

  const hasStoreComment = useMemo(
    () =>
      Boolean(
        (data as any)?.hasStoreComment ??
          commentsList.some((c) => c.userRole === COMMENT_USER_ROLE.STORE)
      ),
    [data, commentsList]
  );

  // Sync initial batch & real-time updates
  useEffect(() => {
    if (isSuccess && data && Array.isArray(data.list)) {
      const serverCount = data.count || 0;
      setTotalCount(serverCount);

      const newestBatchReversed = [...data.list].reverse();

      setCommentsList((prev) => {
        if (prev.length === 0 || !isInitialLoaded) {
          setIsInitialLoaded(true);
          setHasMore(newestBatchReversed.length < serverCount);
          return newestBatchReversed;
        }

        const existingIds = new Set(prev.map((c) => c._id));
        const newIncoming = newestBatchReversed.filter(
          (c) => !existingIds.has(c._id)
        );

        if (newIncoming.length > 0) {
          const updated = [...prev, ...newIncoming];
          setHasMore(updated.length < serverCount);
          return updated;
        }

        setHasMore(prev.length < serverCount);
        return prev;
      });
    }
  }, [data, isSuccess, orderId, isInitialLoaded]);

  // Initial scroll to bottom
  useEffect(() => {
    if (isInitialLoaded && commentsList.length > 0 && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [isInitialLoaded, orderId]);

  // Reset state when switching orders
  useEffect(() => {
    setCommentsList([]);
    setTotalCount(0);
    setIsLoadingMore(false);
    setHasMore(true);
    setIsInitialLoaded(false);
  }, [orderId]);

  // Infinite scroll up handler for older comments
  const handleScroll = async () => {
    const container = threadRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    if (container.scrollTop <= 30) {
      setIsLoadingMore(true);
      const prevScrollHeight = container.scrollHeight;

      try {
        const nextSkip = commentsList.length;
        const res = await fetchCommentsLazy({
          orderId,
          limit: CHAT_PAGE_LIMIT,
          skip: nextSkip,
          sort: "desc",
        }).unwrap();

        if (res && Array.isArray(res.list) && res.list.length > 0) {
          const olderBatchReversed = [...res.list].reverse();

          setCommentsList((prev) => {
            const existingIds = new Set(prev.map((c) => c._id));
            const uniqueOlder = olderBatchReversed.filter(
              (c) => !existingIds.has(c._id)
            );
            const combined = [...uniqueOlder, ...prev];
            setHasMore(combined.length < (res.count || 0));
            return combined;
          });
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Failed to fetch older comments:", err);
      } finally {
        setIsLoadingMore(false);
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }
        });
      }
    }
  };

  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [refetch]);

  const [createComment, { isLoading: createLoading }] =
    useOrderCommentCreateMutation();
  const [toggleImportant] = useOrderCommentImportantMutation();
  const [optimisticImportant, setOptimisticImportant] = useState<{
    [id: string]: boolean;
  }>({});
  const [loadingImportantId, setLoadingImportantId] = useState<string | null>(
    null
  );

  useEffect(() => {
    return () => {
      filePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resetComposer = () => {
    setCommentText("");
    filePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setFilePreviewUrls([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = "26px";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length === 0) return;

    const total = selectedFiles.length + incoming.length;
    if (total > MAX_COMMENT_IMAGES) {
      toastError(ERROR_MESSAGE.MAX_IMAGES_EXCEEDED);
      e.target.value = "";
      return;
    }

    for (const file of incoming) {
      if (!COMMENT_IMAGE_MIME_TYPES.includes(file.type)) {
        toastError(ERROR_MESSAGE.IMAGE_TYPE_INVALID);
        e.target.value = "";
        return;
      }
      if (file.size > MAX_COMMENT_IMAGE_SIZE) {
        toastError(ERROR_MESSAGE.IMAGE_SIZE_EXCEEDED);
        e.target.value = "";
        return;
      }
    }

    const nextFiles = [...selectedFiles, ...incoming];
    setSelectedFiles(nextFiles);
    setFilePreviewUrls(nextFiles.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(filePreviewUrls[index]);
    const nextFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(nextFiles);
    setFilePreviewUrls(nextFiles.map((f) => URL.createObjectURL(f)));
  };

  const canSend =
    (commentText.trim().length > 0 || selectedFiles.length > 0) &&
    !createLoading;

  const handleSend = async () => {
    if (!canSend) return;

    const formData = new FormData();
    formData.append("orderId", orderId);
    formData.append("commentText", commentText.trim());
    selectedFiles.forEach((file) => formData.append("images", file));

    const response = await createComment(formData)
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg);
      });

    if (response && response.status) {
      resetComposer();
      refetch();
      setTimeout(() => {
        if (threadRef.current) {
          threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  const handleToggleImportant = async (comment: Comment) => {
    if (loadingImportantId) return;

    const currentIsImportant =
      optimisticImportant[comment._id] !== undefined
        ? optimisticImportant[comment._id]
        : comment.isImportant;

    const nextState = !currentIsImportant;

    // Instant optimistic UI update (0ms delay)
    setOptimisticImportant((prev) => ({
      ...prev,
      [comment._id]: nextState,
    }));
    setLoadingImportantId(comment._id);

    try {
      const response = await toggleImportant(comment._id).unwrap();
      if (response && response.status) {
        refetch();
      } else {
        setOptimisticImportant((prev) => {
          const copy = { ...prev };
          delete copy[comment._id];
          return copy;
        });
      }
    } catch (err: any) {
      setOptimisticImportant((prev) => {
        const copy = { ...prev };
        delete copy[comment._id];
        return copy;
      });
      toastError(err.data?.errors?.[0]?.msg || "Failed to update status");
    } finally {
      setLoadingImportantId((current) => (current === comment._id ? null : current));
    }
  };

  const openLightbox = (images: CommentImage[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const resolveImageUrl = (url?: string) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${backendUrl}${url}`;
  };

  const renderCommentImages = (comment: Comment) => {
    const images = comment.images || [];
    if (images.length === 0) return null;
    return (
      <div className="d-flex flex-wrap gap-2 mt-2">
        {images.map((img, idx) => (
          <div key={img._id || idx} className="position-relative">
            {img.isPurged ? (
              <div className="comment-image-expired">
                <span>{LABELS.IMAGE_EXPIRED_TEXT}</span>
              </div>
            ) : (
              <img
                src={resolveImageUrl(img.thumbnailUrl || img.imageUrl)}
                alt={img.fileName || `Image ${idx + 1}`}
                loading="lazy"
                className="comment-image-thumb cursor-pointer"
                onClick={() => openLightbox(images, idx)}
              />
            )}
            {!img.isPurged && (
              <a
                className="comment-image-download"
                href={resolveImageUrl(img.imageUrl)}
                download={img.fileName}
                target="_blank"
                rel="noreferrer"
                title={LABELS.DOWNLOAD_IMAGE_TEXT}
              >
                <i className={`icon ${icons.ICON_FILE_PDF}`}></i>
              </a>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getDateGroupLabel = (dateStr?: string) => {
    if (!dateStr) return "";
    const momentDate = moment.utc(dateStr).local();
    if (!momentDate.isValid()) return "";

    const now = moment();
    if (momentDate.isSame(now, "day")) {
      return "Today";
    }
    return momentDate.format("D MMM YYYY");
  };

  return (
    <div className="card section-card order-chat-card mb-3" id="order-chat">
      <div className="card-body">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="order-chat-title-icon-wrap">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <h6 className="section-title mb-0">
            {LABELS.ORDER_COMMUNICATION_TEXT}
          </h6>
        </div>

        {isDispatched && isConfirmed && (
          <div className="alert alert-warning py-2 small">
            {LABELS.COMMENT_READONLY_TEXT}
          </div>
        )}

        <div
          className="order-chat-thread"
          ref={threadRef}
          onScroll={handleScroll}
        >
          {isLoadingMore && (
            <div className="order-chat-loader-top text-center py-2 mb-2">
              <span
                className="spinner-border spinner-border-sm text-secondary"
                role="status"
                aria-hidden="true"
                style={{ width: "16px", height: "16px", borderWidth: "2px" }}
              />
            </div>
          )}
          {commentsList.length === 0 && !isLoading ? (
            <div className="order-chat-empty-state">
              <div className="empty-icon-wrap">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <p className="text-muted text-center mb-0">
                {LABELS.NO_COMMENTS_TEXT}
              </p>
            </div>
          ) : (
            commentsList.map((comment, index) => {
              const isStore = comment.userRole === COMMENT_USER_ROLE.STORE;
              const isRightAligned = isBackOffice
                ? comment.userRole !== COMMENT_USER_ROLE.STORE
                : comment.userRole === COMMENT_USER_ROLE.STORE;
              const currentDateLabel = getDateGroupLabel(comment.createdAt);
              const prevDateLabel =
                index > 0
                  ? getDateGroupLabel(commentsList[index - 1].createdAt)
                  : null;
              const showDateDivider =
                currentDateLabel && currentDateLabel !== prevDateLabel;

              const isImportant =
                optimisticImportant[comment._id] !== undefined
                  ? optimisticImportant[comment._id]
                  : comment.isImportant;
              const isLoadingImportant = loadingImportantId === comment._id;

              return (
                <Fragment key={comment._id || index}>
                  {showDateDivider && (
                    <div className="order-chat-date-divider">
                      <span className="order-chat-date-badge">
                        {currentDateLabel}
                      </span>
                    </div>
                  )}
                  <div
                    className={`order-chat-bubble ${
                      isRightAligned
                        ? "order-chat-align-right"
                        : "order-chat-align-left"
                    } ${isStore ? "order-chat-store" : "order-chat-backoffice"} ${
                      isImportant ? "order-chat-important" : ""
                    }`}
                  >
                    <div className="order-chat-bubble-header d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-1">
                        <span className="order-chat-author">
                          {comment.authorName || LABELS.USER_TEXT}
                        </span>
                        <span
                          className={`order-chat-role ${
                            isStore
                              ? "badge-role-store"
                              : "badge-role-backoffice"
                          }`}
                        >
                          {isStore
                            ? LABELS.ROLE_STORE_TEXT
                            : LABELS.ROLE_BACK_OFFICE_TEXT}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-1 ms-auto">
                        {isImportant && (
                          <span className="order-chat-important-label me-1">
                            IMPORTANT
                          </span>
                        )}
                        {isBackOffice ? (
                          <button
                            type="button"
                            className={`order-chat-important-btn ${
                              isImportant ? "is-important" : ""
                            }`}
                            title={
                              isImportant
                                ? "Unmark Important"
                                : "Mark as Important"
                            }
                            disabled={isLoadingImportant}
                            onClick={() => handleToggleImportant(comment)}
                          >
                            {isLoadingImportant ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  borderWidth: "2px",
                                }}
                              />
                            ) : (
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 24 24"
                                fill={isImportant ? "#d32f2f" : "none"}
                                stroke={isImportant ? "#d32f2f" : "currentColor"}
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill={isImportant ? "#d32f2f" : "none"}
                                />
                                <line
                                  x1="12"
                                  y1="8"
                                  x2="12"
                                  y2="12"
                                  stroke={isImportant ? "#ffffff" : "currentColor"}
                                />
                                <line
                                  x1="12"
                                  y1="16"
                                  x2="12.01"
                                  y2="16"
                                  stroke={isImportant ? "#ffffff" : "currentColor"}
                                />
                              </svg>
                            )}
                          </button>
                        ) : (
                          isImportant && (
                            <span
                              title="Important Comment"
                              className="d-inline-flex align-items-center"
                            >
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 24 24"
                                fill="#d32f2f"
                                stroke="#d32f2f"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="order-chat-important-icon"
                              >
                                <circle cx="12" cy="12" r="10" fill="#d32f2f" />
                                <line
                                  x1="12"
                                  y1="8"
                                  x2="12"
                                  y2="12"
                                  stroke="#ffffff"
                                />
                                <line
                                  x1="12"
                                  y1="16"
                                  x2="12.01"
                                  y2="16"
                                  stroke="#ffffff"
                                />
                              </svg>
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    {comment.images && comment.images.length > 0 ? (
                      <>
                        {comment.commentText && (
                          <div className="order-chat-text mb-1">
                            {comment.commentText}
                          </div>
                        )}
                        <div className="order-chat-body-row d-flex align-items-end justify-content-between gap-3">
                          <div className="flex-grow-1">
                            {renderCommentImages(comment)}
                          </div>
                          <div className="order-chat-time text-nowrap ms-auto mb-1">
                            {comment.createdAt
                              ? moment.utc(comment.createdAt).local().format("hh:mm A")
                              : ""}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="order-chat-body-row d-flex align-items-baseline justify-content-between gap-3 mt-1">
                        <div className="order-chat-text flex-grow-1">
                          {comment.commentText}
                        </div>
                        <div className="order-chat-time text-nowrap ms-auto">
                          {comment.createdAt
                            ? moment.utc(comment.createdAt).local().format("hh:mm A")
                            : ""}
                        </div>
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {isConfirmed &&
          !isDispatched &&
          (isBackOffice && !hasStoreComment ? (
            <div
              className="alert alert-info py-2 px-3 small mt-2 mb-0 d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#f0f7ff",
                borderColor: "#cce5ff",
                color: "#004085",
                borderRadius: "6px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>{LABELS.CHAT_STORE_INITIATE_REQUIRED_TEXT}</span>
            </div>
          ) : (
            <div className="order-chat-composer mt-3">
            {filePreviewUrls.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {filePreviewUrls.map((url, idx) => (
                  <div key={idx} className="preview-thumb-box">
                    <img
                      src={url}
                      alt={`preview-${idx}`}
                      className="comment-image-thumb"
                    />
                    <button
                      type="button"
                      className="comment-image-remove"
                      onClick={() => removeFile(idx)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="order-chat-composer-row">
              <label
                className="order-chat-paperclip-btn mb-0"
                title={LABELS.ATTACH_IMAGE_TEXT}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                </svg>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  multiple
                  hidden
                  onChange={handleFileSelect}
                  disabled={selectedFiles.length >= MAX_COMMENT_IMAGES}
                />
              </label>

              <div className="order-chat-pill-wrapper">
                <textarea
                  ref={textareaRef}
                  className="order-chat-pill-input"
                  placeholder={LABELS.COMMENT_PLACEHOLDER_TEXT}
                  value={commentText}
                  rows={1}
                  maxLength={1000}
                  onChange={(e) => {
                    setCommentText(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(
                      e.target.scrollHeight,
                      120
                    )}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
              </div>

              <button
                type="button"
                className="order-chat-send-icon-btn"
                onClick={handleSend}
                disabled={!canSend || createLoading}
                title={BUTTON_LABEL.SEND}
              >
                {createLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ width: "18px", height: "18px", borderWidth: "2px" }}
                  />
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                )}
              </button>
            </div>
            </div>
          ))}
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((i) => Math.max(0, (i || 0) - 1))}
          onNext={() =>
            setLightboxIndex((i) =>
              Math.min(lightboxImages.length - 1, (i || 0) + 1)
            )
          }
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
};

export default OrderChat;
